import { T, Var, useGT } from 'gt-react';
import { formatDistanceToNow } from 'date-fns';
import type { ArTaskChannel, ArTaskRow } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useErpName } from '@/api/org/config/useErpName';
import { TaskAssigneeControl } from '@/pages/authenticated/tasks/$taskId/TaskReviewControls';
import type { TaskReviewLoadedProps } from '@/pages/authenticated/tasks/$taskId/TaskReviewLoadedProps';
import { useTaskReviewModel } from '@/pages/authenticated/tasks/$taskId/useTaskReviewModel';
import { TaskReviewLoadNotice } from '@/pages/authenticated/tasks/$taskId/TaskReviewBody';
import {
  formatLogTaskStatus,
  logTaskStatusClass,
} from '@/components/accounts-receivable/cash-receipt-detail/cashReceiptTaskFormat';
import { issueLabel, referenceLabel } from './arTasksColumns';
import {
  arTaskKindOfType,
  arTaskRecord,
  isOpenTaskStatus,
  recordReference,
  type ArTaskRecord,
} from './arTasksPageState';
import { ArTaskRecordPanel } from './ArTaskRecordPanel';
import { ArTaskNotesFeed } from './ArTaskNotesFeed';
import { ArTaskStatusTimeline } from './ArTaskStatusTimeline';
import { arTaskRecordFeedSources } from './arTaskFeed';
import { useArTaskRecords } from './useArTaskRecords';

interface ArTaskDetailActions {
  canManageTasks: boolean;
  onCloseTask: (row: ArTaskRow) => void;
  /** Opens the task's invoice or sales order in its slide-in. */
  onOpenRecord: (record: ArTaskRecord) => void;
}

/** Why a not-sent task is open and what closes it, by the customer's channel. */
function NotSentReason({ channel }: { channel: ArTaskChannel | undefined }) {
  const erpName = useErpName();
  switch (channel) {
    case 'portal':
      return (
        <T context="AR task detail, why a portal customer's unsent-invoice task is open and when it closes; the variable is the ERP's name, e.g. Famous">
          This is a portal customer and we didn't see a note about it on the
          invoice. Closes on its own once a portal note is added in{' '}
          <Var>{erpName}</Var>.
        </T>
      );
    case 'edi':
      return (
        <T context="AR task detail, why an EDI customer's unsent-invoice task is open and when it closes; the variable is the ERP's name, e.g. Famous">
          This is an EDI customer and no EDI invoice was sent or noted. Closes
          on its own once the EDI invoice is sent or noted in{' '}
          <Var>{erpName}</Var>.
        </T>
      );
    case 'unknown':
      return (
        <T context="AR task detail, unsent-invoice task for a new customer whose invoicing channel is not known">
          We don't know yet how this customer gets invoices. Closes on its own
          once the invoice is sent.
        </T>
      );
    default:
      return (
        <T context="AR task detail, when an unsent-invoice task closes">
          Closes on its own once the invoice is sent.
        </T>
      );
  }
}

/**
 * When the task goes away by itself: the tab's feeding workflow (Famous)
 * stops reporting it.
 */
function ClosesOnItsOwn({
  kind,
  channels,
}: {
  kind: ArTaskRow['kind'];
  channels: ArTaskRow['channels'] | undefined;
}) {
  const erpName = useErpName();
  return (
    <p className="m-0 text-xs text-muted-foreground">
      {kind === 'to_send' ? (
        <NotSentReason
          channel={channels?.length === 1 ? channels[0] : undefined}
        />
      ) : (
        <T context="AR task detail, when a not-invoiced task closes; the variable is the ERP's name, e.g. Famous">
          Closes on its own once the invoice syncs from <Var>{erpName}</Var>.
        </T>
      )}
    </p>
  );
}

/**
 * Header: id and age; the record and customer with the issue (Invoiced
 * but not sent / Shipped but not invoiced) and the task's status as
 * badges; when the task closes on its own; then Assignee and Close Task.
 * No Send Invoice: Famous decides when an invoice is sent.
 */
function DetailHeader({
  loaded,
  row,
  label,
  kind,
  actions,
}: {
  loaded: TaskReviewLoadedProps;
  row: ArTaskRow | undefined;
  label: string;
  kind: ArTaskRow['kind'] | undefined;
  actions: ArTaskDetailActions;
}) {
  const gt = useGT();
  const { task } = loaded;
  const title = [label, row?.customerName].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b py-3 pl-5 pr-12">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{task.displayId}</span>
          <span aria-hidden>·</span>
          <span>
            <T context="AR task detail, when the detector opened the task">
              opened{' '}
              <Var>
                {formatDistanceToNow(new Date(task.createdAt), {
                  addSuffix: true,
                })}
              </Var>
            </T>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <h2 className="m-0 truncate text-lg font-semibold">
            {title || task.title}
          </h2>
          {kind && (
            <Badge
              variant="outline"
              className="border-amber-300 bg-amber-50 text-xs font-semibold text-amber-800"
            >
              {issueLabel({ kind, channels: row?.channels }, gt)}
            </Badge>
          )}
          <Badge
            variant="outline"
            className={`text-xs ${logTaskStatusClass(task.status)}`}
          >
            {formatLogTaskStatus(task.status, gt)}
          </Badge>
        </div>
        {kind && <ClosesOnItsOwn kind={kind} channels={row?.channels} />}
      </div>
      <HeaderActions loaded={loaded} row={row} actions={actions} />
    </div>
  );
}

/**
 * Assignee and Close Task. The row can be a remembered one (the task left
 * the page), so Close Task also checks the loaded task is still open.
 */
function HeaderActions({
  loaded,
  row,
  actions,
}: {
  loaded: TaskReviewLoadedProps;
  row: ArTaskRow | undefined;
  actions: ArTaskDetailActions;
}) {
  const { task } = loaded;
  const canClose =
    row && actions.canManageTasks && isOpenTaskStatus(task.status);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-48 shrink-0 [&_.min-w-36]:min-w-0 [&_button]:h-8">
        <TaskAssigneeControl
          task={task}
          users={loaded.users}
          agents={loaded.agents}
        />
      </div>
      {canClose && (
        <Button variant="outline" onClick={() => actions.onCloseTask(row)}>
          <T context="button, close an AR task from its detail view">
            Close Task
          </T>
        </Button>
      )}
    </div>
  );
}

/**
 * The detail pane of the AR Tasks list-detail split, one page with no tabs:
 * header with actions; why the task is open; the task's invoice or sales
 * order's ERP history across the top; under it, the order's detail view on
 * the left and one notes and activity feed on the right.
 */
export function ArTaskDetail({
  taskId,
  row,
  ...actions
}: {
  taskId: string;
  /** The table row for this task, when it is on the current page. */
  row: ArTaskRow | undefined;
} & ArTaskDetailActions) {
  const model = useTaskReviewModel({ taskId });
  const loadedTask = model.kind === 'ready' ? model.loaded.task : undefined;
  const record = arTaskRecord(row, loadedTask);
  const records = useArTaskRecords(record);
  const { invoice, salesOrder } = records;
  // A task whose record hasn't synced yet still has its row's number.
  const reference =
    row?.reference ??
    recordReference(record, {
      invoiceNumber: invoice?.invoiceNumber,
      originId: salesOrder?.originId,
    });
  const kind =
    record?.kind ??
    row?.kind ??
    arTaskKindOfType(
      model.kind === 'ready' ? model.loaded.taskType?.key : undefined,
    );
  const label = kind && reference ? referenceLabel({ kind, reference }) : '';
  if (model.kind !== 'ready') {
    return <TaskReviewLoadNotice state={model.kind} />;
  }
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <DetailHeader
        loaded={model.loaded}
        row={row}
        label={label}
        kind={kind}
        actions={actions}
      />
      <div className="flex shrink-0 flex-col gap-6 p-5">
        {kind && <ArTaskStatusTimeline taskId={taskId} />}
        <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {kind && (
            <ArTaskRecordPanel
              kind={kind}
              record={record}
              records={records}
              onOpenRecord={actions.onOpenRecord}
            />
          )}
          <ArTaskNotesFeed
            // A new task starts with an empty draft, so a half-written note
            // can't be posted to the next task.
            key={model.loaded.task.id}
            loaded={model.loaded}
            sources={{
              ...arTaskRecordFeedSources(kind, records),
              rowNote: row?.invoiceNote,
            }}
          />
        </div>
      </div>
    </div>
  );
}
